<template>
  <div class="container-relations">
    <div class="containers-div">
      <div class="header flex-1to1">
        <ul class="nav flex-3 mt5">
          <li><a href="#relations" :class="{ 'active': tab == 'hierarchies' }" @click="() => onChangeTab('hierarchies')">Hierarchies</a></li>
          <li><a href="#relations" :class="{ 'active': tab == 'associations' }" @click="() => onChangeTab('associations')">Associations</a></li>
          <li><a href="#relations" :class="{ 'active': tab == 'groups' }" @click="() => onChangeTab('groups')">Groups</a></li>
        </ul>
        <div>
          <info-tip 
            :tip="toolTip()"
            :show-icon="false"
            class="pull-right"
          >          
            <button v-if="tab == 'hierarchies'" class="btn btn-carma btn-square pull-right" @click="onAddRelationship('hierarchy')" :disabled="isRelationLoading">
              <i class="fas fa-plus"></i> Add Hierarchy
            </button>
            <button v-if="tab == 'associations'" class="btn btn-carma btn-square pull-right" @click="onAddRelationship('association')" :disabled="isRelationLoading">
              <i class="fas fa-plus"></i> Add Association
            </button>
            <button v-if="tab == 'groups'" class="btn btn-carma btn-square pull-right" @click="onAddRelationship('group')" :class="{ 'disabled': isGroupingCategory() }" >
              <i class="fas fa-plus"></i> Add Group
            </button>
          </info-tip>
        </div>
      </div>
      <load-content :key="renderKey" :loading="((isRelationLoading && relationPagination.page === 1) || !relationData || projectSetupIsBusy) && tab !== 'groups'">

        <div ref="hierarchies" v-if="tab == 'hierarchies'" class="card">
          <h3>{{ $t('tag.labels.hierarchies') }}</h3>
          <RelationTag
            v-for="(relation) in getRelationData"
            :key="`relation_hierarchies_${relation.parent_tag_id}_${tagsIds(relation)}`"
            relationType="hierarchy"
            :relation="relation"
            :onEdit="(relation) => onEdit('hierarchy', relation)"
            :onOpen="onOpen"
            :onRemove="(relation) => onRemove('hierarchy', relation)"
          />
        </div>
        <div ref="associations" v-if="tab == 'associations'" class="card">
          <div class="associations-header">
            <h3>{{ $t('tag.labels.associations') }}</h3>
            <button 
              class="btn btn-square btn-default"
              @click="exportAssociations"
              :disabled="isRelationLoading || !getRelationData || getRelationData.length === 0"
            >
              <i class="fas fa-file-excel"></i> {{ $t('tag.labels.export_associations') }}
            </button>
          </div>
          
          <!-- Search bar for tag associations -->
          <div v-if="isUseOrgWithCategory" class="search-controls">
            <div class="filter-search">
              <div class="search-wrapper">
                <InputText 
                  type="search" 
                  id="associations-search" 
                  class="input-ui border search-input"
                  :placeholder="$t('tag.placeholders.search_tags') + '...'"
                  v-model="searchQuery"
                  @keyup.enter.native="onSearchSubmit"
                  :onChange="() => {}"
                  :useDebounce="false"
                  :onBlurOnly="false"
                />
                <span class="fa fa-search" @click="onSearchSubmit"></span>
                <span v-if="searchQuery" class="fa fa-times clear-search" @click="onClearSearch"></span>
              </div>
            </div>
            
            <!-- Search scope selection -->
            <div class="search-scope">
              <label class="scope-label">{{ $t('tag.labels.search_in') }}</label>
              <div class="scope-options">
                <label class="scope-option">
                  <input 
                    type="radio" 
                    v-model="searchScope" 
                    :value="TAG_SEARCH_SCOPE.PARENT"
                    name="searchScope"
                  />
                  <span>{{ $t('tag.labels.parent_tags') }}</span>
                </label>
                <label class="scope-option">
                  <input 
                    type="radio" 
                    v-model="searchScope" 
                    :value="TAG_SEARCH_SCOPE.CHILDREN"
                    name="searchScope"
                  />
                  <span>{{ $t('tag.labels.child_tags') }}</span>
                </label>
                <label class="scope-option">
                  <input 
                    type="radio" 
                    v-model="searchScope" 
                    :value="TAG_SEARCH_SCOPE.BOTH"
                    name="searchScope"
                  />
                  <span>{{ $t('tag.labels.both') }}</span>
                </label>
              </div>
            </div>
          </div>

          <!-- Filters Section (Date and User) -->
          <div v-if="isUseOrgWithCategory" class="filters-row">
            <!-- Date Filter -->
            <div class="date-filter-wrapper">
              <div class="date-type-selector">
                <InputSelect
                  v-model="dateType"
                  :searchable="false"
                  :options="dateTypeOptions"
                  :allowEmpty="false"
                  :white-background="true"
                  class="date-type-dropdown"
                />
              </div>
              <div class="date-range-wrapper">
                <DateRangePicker
                  name="associations-date-range"
                  :value="dateRange"
                  inputType="date"
                  valueType="YYYY-MM-DD HH:mm:00"
                  :range="true"
                  format="MMM DD YYYY"
                  :clearable="true"
                  range-separator=" - "
                  @onInputChange="onDateRangeChange"
                  calendarButtonIcon="fas fa-calendar-alt"
                  :disabled="isRelationLoading"
                />
              </div>
            </div>

            <!-- User Filter -->
            <div class="user-filter-wrapper">
              <div class="user-type-selector">
                <InputSelect
                  v-model="userFilterType"
                  :searchable="false"
                  :options="userFilterTypeOptions"
                  :allowEmpty="false"
                  :white-background="true"
                  class="user-type-dropdown"
                />
              </div>
              <div class="user-selector-container">
                <InputSelect
                  v-model="selectedUser"
                  :searchable="true"
                  :options="userOptions"
                  :allowEmpty="true"
                  :placeholder="$t('tag.placeholders.select_user')"
                  :white-background="true"
                  :loading="isUsersLoading"
                  class="user-dropdown"
                />
                <i 
                  v-if="selectedUser"
                  class="fas fa-times clear-user-filter"
                  @click="onClearUserFilter"
                  :title="$t('tag.clear_user_filter')"
                ></i>
              </div>
            </div>
          </div>
          
          <!-- Total count display -->
          <div v-if="isUseOrgWithCategory && getRelationPagination && getRelationPagination.totalCount" class="total-count-display">
            <span class="count-text">
              {{ $t('tag.labels.total_results') }}: 
              <strong>{{ getRelationPagination.totalCount }}</strong>
            </span>
          </div>
          
          <div class="relations-table">
            <table class="table table--datatable associations-table">
              <thead>
                <tr>
                  <th style="width: 13%">{{ $t('tag.labels.parent_tags') }}</th>
                  <th style="width: 35%">{{ $t('tag.labels.child_tags') }}</th>
                  <th style="width: 11%">{{ $t('tag.labels.updated_at') }}</th>
                  <th style="width: 9%">{{ $t('tag.labels.updated_by') }}</th>
                  <th style="width: 11%">{{ $t('tag.labels.created_at') }}</th>
                  <th style="width: 9%">{{ $t('tag.labels.created_by') }}</th>
                  <th style="width: 4%">{{ $t('tag.labels.actions') }}</th>
                </tr>
              </thead>
              <tbody v-if="!isRelationLoading || (isRelationLoading && getRelationData.length > 0)">
                <template v-for="relation in getRelationData">
                  <tr 
                    v-for="(child, index) in (relation.tags || relation.child_tags || [])"
                    :key="`${relation.id}_${child.temp_id || index}`"
                    class="child-row"
                    :class="{ 'last-child-of-parent': index === (relation.tags || relation.child_tags || []).length - 1 }"
                  >
                    <td v-if="index === 0" :rowspan="(relation.tags || relation.child_tags || []).length">
                      <div class="parent-tag-with-category">
                        <div>{{ relation.tag_name }}</div>
                        <div class="category-badge">
                          ({{ relation.category_name || relation.parent_category_name || '-' }})
                        </div>
                      </div>
                    </td>
                    <td>
                      <div class="chid-tag-with-category">
                        <div>{{ child.name }}</div>
                        <div class="category-badge">
                          ({{ child.category_name }})
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="metadata-value">{{ formatDate(child.updated_at) || 'N/A' }}</span>
                    </td>
                    <td>
                      <span class="metadata-value">{{ child.updated_by || 'N/A' }}</span>
                    </td>
                    <td>
                      <span class="metadata-value">{{ formatDate(child.created_at) || 'N/A' }}</span>
                    </td>
                    <td>
                      <span class="metadata-value">{{ child.created_by || 'N/A' }}</span>
                    </td>
                    <td>
                      <div class="actions">
                        <RelationDeleteButton
                          :relation="relation"
                          :child="child"
                          relationType="association"
                          :disabled="projectSetupIsBusy"
                          :onRemove="onRemove"
                        />
                        <RelationEditButton
                          v-if="index === 0"
                          :relation="relation"
                          relationType="association"
                          :disabled="projectSetupIsBusy"
                          :onEdit="onOpen"
                        />
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
            <div v-if="getRelationData.length == 0 && !isRelationLoading" class="empty-state">
              <div class="icon"><i class="fas fa-search"></i></div>
              <div>{{ $t('tag.labels.no_associations') }}</div>
            </div>
            <loader v-if="isRelationLoading && relationPagination.page === 1" />
          </div>
          <div class="loading-next-page" v-if="isRelationLoading && relationPagination.page > 1"><loader /></div>
          <div class="mt20">
            <span v-if="getRelationData && getRelationData.length > 5" class="span-add-row" @click="onAddRelationship('association')">
              <i class="fas fa-plus button btn-primary pr5" aria-hidden="true"></i>
              <b>{{ $t('tag.labels.add_association') }}</b>
            </span>
          </div>
        </div>
      </load-content>

      <div v-if="tab == 'groups'" class="card">
        <load-content :loading="isBusy">
          <datatable
            :data="groupingsData"
          />
        </load-content>
      </div>
    </div>

    <side-panel v-if="showHierarchyPanel" :onClose="closePanel">
      <template>
        <load-content :loading="isBusy">
          <div id="side-panel-content">
            <form-add-update-relationship
              relationshipType="hierarchy"
              :relationship="edit"
              :categories="categories"
              :hierarchies="getRelationData"
              :onSave="onSaveRelationship"
              :onRemove="async (relation) => await onConfirmRemove('hierarchy', relation)"
              :general="general"
              :isEdit="isEdit"
            />
          </div>
        </load-content>
      </template>
    </side-panel>

    <side-panel v-if="showAssociationPanel" :onClose="closePanel">
      <template>
        <form-tag-association
          relationshipType="association"
          :relationship="edit"
          :categories="categories"
          :associations="getRelationData"
          :onSave="onSaveRelationship"
          :isEdit="isEdit"
        />
      </template>
    </side-panel>

    <side-panel v-if="showRemoveRelationshipPanel" :onClose="closePanel">
      <template>
        <load-content :loading="isBusy">
          <div id="side-panel-content">
            <h3>Delete relationship</h3>
            <p>Are you sure that you want to delete this relationship?</p>

            <div class="text">
              <div><b>Child: </b> {{ removeAssociationName }}</div>
              <div v-if="edit.name"><b>Parent: </b> {{ edit.name }}</div>
              <div v-if="edit.associated_tag_name"><b>Association: </b> {{edit.associated_tag_name}}</div>
            </div>

            <div class="btn btn-danger btn-sm" @click="onConfirmRemove(edit.parent_tag_id ? 'hierarchy' : 'association', edit)">Confirm delete</div>
          </div>
        </load-content>
      </template>
    </side-panel>

    <side-panel v-if="showGroupPanel" :onClose="closePanel">
      <template>
        <load-content :loading="isBusy">
          <div id="side-panel-content">
            <form-tag-create
              :loading="isBusy"
              :errors="formTagCreateErrors"
              :start-category-type-id="categoryTypeId"
              @addTag="addTag"
            />
          </div>
        </load-content>
      </template>
    </side-panel>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import SidePanel from '../side-panel';
import { FIELD_TYPE, CATEGORY_TYPES, TAG_SEARCH_SCOPE } from "../../config/const";
import LoadContent from '../load-content';
import FormAddUpdateRelationship from './form-add-update-relationship';
import FormTagAssociation from './form-tag-association';
import RelationTag from './relation-tag';
import Datatable from '../datatable';
import GroupingActions from './grouping-actions';
import runShortcut from "../../mixins/shortcuts";
import { mapActions, mapGetters, mapState } from "vuex";
import InfoTip from "../info-tip";
import FormTagCreate from "./form-tag";
import Loader from "../loader";
import RelationEditButton from "./relation-edit-button";
import RelationDeleteButton from "./relation-delete-button";
import InputText from "../form-components/input-text";
import DateRangePicker from "../date-picker-2";
import InputSelect from "../form-components/input-select";

export default {
  props: {
    data: Object,
    isBusy: Boolean,
    addRelationshipRecord: Function,
    updateRelationshipRecord: Function,
    removeRelationshipRecord: Function,
    updateGroupingTags: Function,
    sortGroupingTable: Function,
    general: Object
  },

  mounted: function() {
    this.projectUid = this.$route.params.project_uid
    if(this.projectSetup?.general?.organizations_with_category) {
      this.getProjectCategoriesSetup({ projectUid: this.projectUid });
      const { relationPagination, projectUid } = this;
      this.getRelation({ projectUid: projectUid, pagination: relationPagination });
    }
    
    this.fetchProjectUsers();

    window.addEventListener('scroll', this.scroll);

    this.categoryTypeId = CATEGORY_TYPES.GROUPING;
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.scroll);
  },

  data: function() {
    return {
      projectUid: undefined,
      tab: 'hierarchies',
      showHierarchyPanel: false,
      showAssociationPanel: false,
      showGroupPanel: false,
      showRemoveRelationshipPanel: false,
      formTagCreateErrors: [],
      categoryTypeId: undefined,
      currentTab: 1,
      edit: null,
      error: '',
      searchQuery: '',
      searchScope: TAG_SEARCH_SCOPE.PARENT, // Default to parent tag search using constant
      searchFilters: null, // Store current search filters
      dateType: 'created_at',
      dateFrom: null,
      dateTo: null,
      dateTypeOptions: [
        {
          label: this.$t('tag.labels.created_at'),
          value: 'created_at',
        },
        {
          label: this.$t('tag.labels.updated_at'),
          value: 'updated_at',
        }
      ],
      userFilterType: 'created_by',
      userFilterTypeOptions: [
        {
          label: this.$t('tag.labels.created_by'),
          value: 'created_by',
        },
        {
          label: this.$t('tag.labels.updated_by'),
          value: 'updated_by',
        },
        {
          label: this.$t('tag.labels.updated_by_or_created_by'),
          value: 'both',
        }
      ],
      selectedUser: null,
      isUsersLoading: false,
      pagination: {
        page: 1,
        perPage: 20,
        order: "group_id",
        sort: "asc"
      },
      relationPagination: {
        page: 1,
        perPage: 25,
        order: "id",
        sort: "asc"
      },
      isEdit: false,
      renderKey: 0,
      TAG_SEARCH_SCOPE: TAG_SEARCH_SCOPE
    };
  },

  watch: {
    "relationPagination": {
      handler(value){
        const { relationPagination, projectUid, searchFilters } = this;
        if(!this.isRelationLoading && this.isUseOrgWithCategory) {
          this.getRelation({
            projectUid: projectUid, 
            pagination: relationPagination,
            filters: searchFilters
          });
        }
      },
      deep:true
    },
    "searchScope": {
      handler(newScope, oldScope) {
        // Only trigger search if we have a search query and scope actually changed
        if (this.searchQuery && newScope !== oldScope) {
          this.performSearch();
        }
      }
    },
    "dateType": {
      handler(newType, oldType) {
        // Only trigger search if dates are selected and type actually changed
        if ((this.dateFrom || this.dateTo) && newType !== oldType) {
          this.performSearch();
        }
      }
    },
    "userFilterType": {
      handler(newType, oldType) {
        // Only trigger search if user is selected and type actually changed
        if (this.selectedUser && newType !== oldType) {
          this.performSearch();
        }
      }
    },
    "selectedUser": {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.performSearch();
        }
      }
    },
    "projectAssociation": {
      handler(value){
        this.renderKey += 1;
      },
      deep:true
    },
    "projectHierarchy": {
      handler(value){
        this.renderKey += 1;
      },
      deep:true
    }
  },

  computed: {
    ...mapState({
      projectCategories: state => state.projectSetup.projectCategorySetup?.data || [],
      projectSetup: state => state.projectSetup.projectSetup.data,
      projectHierarchy: state => state.projectSetup.projectHierarchy?.data,
      projectHierarchyPagination: state => state.projectSetup.projectHierarchy?.pagination,
      projectHierarchyIsLoading: state => state.projectSetup.projectHierarchy?.isBusy,
      projectAssociation: state => state.projectSetup.projectAssociation?.data,
      projectAssociationPagination: state => state.projectSetup.projectAssociation?.pagination,
      projectAssociationIsLoading: state => state.projectSetup.projectAssociation?.isBusy,
      userCoder: state => state.user.userCoder.data || []
    }),
    ...mapGetters({
      addTagsCategories: "projectSetup/CATEGORIES_CAN_ADD_TAGS",
      projectSetupIsBusy: "projectSetup/project_setup_is_busy"
    }),
    dateRange() {
      return [
        this.dateFrom ? moment(this.dateFrom).startOf('day').format('YYYY-MM-DD HH:mm:00') : null,
        this.dateTo ? moment(this.dateTo).endOf('day').format('YYYY-MM-DD HH:mm:00') : null
      ];
    },
    userOptions() {
      if (!this.userCoder || this.userCoder.length === 0) {
        return [];
      }
      return this.userCoder.map(user => ({
        label: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || user.username || `User ${user.id}`,
        value: user.user_id
      }));
    },
    categories: function() {
      return this.projectCategories.filter(c => c.category_type_id != 5 && c.category_type_id != 6);
    },
    isUseOrgWithCategory() { return this.projectSetup?.general?.organizations_with_category },
    getRelation() {
      switch (this.tab) {
        case 'hierarchies':
          return this.getProjectHierarchy;
        case 'associations':
          return this.getProjectAssociation;
      
        default:
          return () => {};
      }
    },
    isRelationLoading() {
      switch (this.tab) {
        case 'hierarchies':
          return this.projectHierarchyIsLoading;
        case 'associations':
          return this.projectAssociationIsLoading;
      
        default:
          return false;
      }
    },
    getRelationPagination() {
      switch (this.tab) {
        case 'hierarchies':
          return this.projectHierarchyPagination;
        case 'associations':
          return this.projectAssociationPagination;
      
        default:
          return null;
      }
    },
    relationData() {
      switch (this.tab) {
        case 'hierarchies':
          return this.projectHierarchy;
        case 'associations':
          return this.projectAssociation;
      
        default:
          return null;
      }
    },
    getRelationListRef() {
      switch (this.tab) {
        case 'hierarchies':
          return this.$refs.hierarchies;
        case 'associations':
          return this.$refs.associations;
      
        default:
          return {};
      }
    },

    removeAssociationName() {
      if (this.edit && this.edit.childName) return this.edit.childName;

      if (this.edit && this.edit.parent_tag_name) return this.edit.parent_tag_name;

      return '-';
    },

    getRelationData: function() {
      let data = this.relationData;
      const filteredData = data?.filter(h => h.child_tags?.length > 0)
        .map(h => {
          h.child_tags.forEach(t => {
            t.temp_id = this.generateTempId()
          })
          return h;
        });

      return filteredData;
    },

    groupings: function() {
      let { groupings } = this.data;
      groupings.categories = this.categories.filter(c => c.category_type_id != 5 && c.category_type_id != 6);
      return groupings;
    },

    groupingsData () {
      let { groupings } = this.data;
      const categories = [...this.projectCategories];
      const self = this;

      return {
        body: this.groupings,
        columns: [
           {
            key: 'category_name',
            label: 'Category',
            orderable: true,
            input: {
              name: "category_name",
              type: FIELD_TYPE.INPUT,
              styleNames: { color: "#444", border: "none", fontSize: '12px', outline: 'none' },
              readOnly: true,
            },
          },
          {
            key: 'group_name',
            label: 'Group',
            input: {
              name: "group_name",
              type: FIELD_TYPE.INPUT,
              styleNames: { color: "#444", border: "none", fontSize: '12px', outline: 'none' },
              readOnly: true,
            },
          },
          {
            key: 'group_id',
            style: {
              width: '40px'
            },
            render: (row) => {
              const grouping = groupings && row.group_id && groupings.find(g => g.group_id == row.group_id);
              let data = {};
              if (grouping) {
                data = {
                  ...grouping,
                  grouping_tags: grouping.grouping_tags.map((t, i) => ({ ...t, tempId: i }))
                }
              }              
              return (<GroupingActions grouping={data} categories={categories} tags={row.grouping_tags} isBusy={this.isBusy} onSave={this.onSaveGrouping} />);
            }
          }
        ],
        order: {
          column: self.pagination.order,
          direction: self.pagination.sort,
          onChange: function(column, direction) {
            self.pagination = Object.assign({}, self.pagination , {order:column, sort: direction});
            return self.sortGroupingTable(self.pagination);
          }
        },
      }
    }
  },

  methods: {
    ...mapActions({
      getprojectSetup: "projectSetup/getprojectSetup",
      createCategoryGrouping: "projectSetup/createCategoryGrouping",
      getProjectCategoriesSetup: "projectSetup/getProjectCategoriesSetup",
      getProjectHierarchy: "projectSetup/getProjectHierarchy",
      getProjectAssociation: "projectSetup/getProjectAssociation",
      exportProjectAssociations: "projectSetup/exportProjectAssociations",
      getUserCoder: "user/getUserCoder"
    }),

    fetchProjectUsers: function() {
      if (!this.projectUid) return;
      
      this.isUsersLoading = true;
      this.getUserCoder({ projectId: this.projectUid })
        .finally(() => {
          this.isUsersLoading = false;
        });
    },

    exportAssociations: function() {
      const { projectUid } = this;
      
      const exportParams = {
        projectUid: projectUid,
        search: this.searchQuery.trim(),
        searchScope: this.searchScope
      };

      // Add date filters if they exist
      if (this.dateFrom) {
        exportParams.date_from = moment(this.dateFrom).startOf('day').format('YYYY-MM-DDTHH:mm');
      }
      if (this.dateTo) {
        exportParams.date_to = moment(this.dateTo).endOf('day').format('YYYY-MM-DDTHH:mm');
      }
      if (this.dateFrom || this.dateTo) {
        exportParams.date_type = this.dateType;
      }

      // Add user filters if they exist
      if (this.selectedUser) {
        if (this.userFilterType === 'both') {
          exportParams.created_by = this.selectedUser;
          exportParams.updated_by = this.selectedUser;
        } else if (this.userFilterType === 'created_by') {
          exportParams.created_by = this.selectedUser;
        } else if (this.userFilterType === 'updated_by') {
          exportParams.updated_by = this.selectedUser;
        }
      }

      this.exportProjectAssociations(exportParams);
    },

    onEdit: function(/*type, relation*/) {
    },

    onRemove: function(type, relation) {
      this.edit = relation;
      this.showRemoveRelationshipPanel = true;
    },

    onConfirmRemove: async function(type, relation) {
      return this.removeRelationshipRecord(type, relation);
    },

    onOpen: function(type, relation, tag) {
      if (type == 'hierarchy') {
        this.showHierarchyPanel = true;
      } else if (type == 'association') {
        this.showAssociationPanel = true;
      }

      // get/compose child tag for edit
      let _relation = _.cloneDeep(relation);
      if(tag){
        // get child data from the hierarchy
        const childTag = _.cloneDeep(this.projectHierarchy)?.reduce((acc, cate) => cate?.parent_tag_category_id === _relation?.parent_tag_category_id && (acc = cate?.tags?.find(t => t?.id === tag?.id && (t.category_id = _relation?.category_id || t?.cat_id)) || acc) || acc, {});
        if(Object.keys(childTag)?.length && Array.isArray(_relation.child_tags)) _relation.child_tags = [ ..._relation.child_tags?.filter(ct => ct?.id !== childTag?.id), childTag ];
        this.isEdit = true;
      }

      this.edit = _relation;
    },

    closePanel: function() {
      this.showHierarchyPanel = false;
      this.showAssociationPanel = false;
      this.showGroupPanel = false;
      this.showRemoveRelationshipPanel = false;
      this.isEdit = false;
    },

    addRow: function(){
      if(this.tab == 'hierarchies') this.onAddRelationship("hierarchy");
      else if(this.tab == 'associations') this.onAddRelationship("association");
      else if(this.tab == 'groups') this.onAddRelationship("group");
    },

    onAddRelationship: function(type) {
      if (type == 'hierarchy') {
        this.edit = {
          create: true,
          tags: [],
          parent_tag_id: null,
          parent_tag_category_id: null,
          category_id: null,
          child_tags: []
        }

        this.showHierarchyPanel = true;
      } else if (type === 'association') {
        this.edit = {
          create: true,
          tag_id: null,
          associated_tag_id: null,
          associated_tag_category_id: null,
          category_id: null,
          child_tags: []
        }

        this.showAssociationPanel = true;
      } else if (type === 'group') {
        this.edit = {
          create: true,
          tag_id: null,
          associated_tag_id: null,
          associated_tag_category_id: null,
          category_id: null,
          child_tags: []
        }

        this.showGroupPanel = true;
      }
    },

    onSaveRelationship: function(type, relationship) {
      if (relationship.create) {
        return this.addRelationshipRecord(type, relationship);
      }
      else {
        return this.updateRelationshipRecord(type, relationship);
      }
    },

    generateTempId: function() {
      return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
      );
    },

    onChangeTab: function(tab) {
      if(this.isRelationLoading) return;
      this.tab = tab;
      // Clear search, date, and user filters when switching tabs
      this.searchQuery = '';
      this.searchFilters = null;
      this.dateFrom = null;
      this.dateTo = null;
      this.selectedUser = null;
      const { relationPagination, projectUid} = this;
      if(this.isUseOrgWithCategory) {
        this.getRelation({
          projectUid: projectUid, 
          pagination: { ...relationPagination, page: 1 }
        });
      }
    },

    addTag: function (tag) {
      const row = {
        cat_id: tag.categoryId,
        categoryId: tag.categoryId,
        ...tag
      };

      this.currentTab = tag.categoryId;
      this.formTagCreateErrors = [];

      this.createCategoryGrouping({
        projectUid: this.projectUid,
        row
      })
        .then(() => {
          this.closePanel();
          this.sortGroupingTable();
        })
        .catch(error => {
          this.formTagCreateErrors.push(error);
        });
    },

    onSaveGrouping: function (grouping) {
      return this.updateGroupingTags(grouping);
    },
    isGroupingCategory() {
      if (!this.addTagsCategories) return true; 
      return !this.addTagsCategories.some(category => category.category_type_id === this.categoryTypeId);
    },
    toolTip () {
      if(this.tab == 'groups' && this.isGroupingCategory()){
        return this.$i18n.t("tag.tooltips.missing_category");
      }
      return this.shortcutKeyCodeLabel;
    },
    tagsIds(relation){
      if(!relation) return '';
      let _tags = relation?.tags?.length ? relation?.tags : relation?.child_tags?.length ? relation?.child_tags : [];
      return _tags?.map(t => t?.id || '')?.join('_') || '';
    },
    scroll: function() {
      if (this.relationData) {
        const { currentPage, totalPages } = this.getRelationPagination;

        if (currentPage < totalPages) {
          const wHeight = window.innerHeight;
          const {
            top,
            bottom,
            height
          } = this.getRelationListRef?.getBoundingClientRect();
          const margin = 100;

          if (top * -1 + wHeight >= height - margin && this.isRelationLoading === false) {
            const { relationPagination } = this;
            this.relationPagination = {
              ...relationPagination,
              page: relationPagination.page + 1
            }
          }
        }
      }
    },

    // Search functionality methods
    onSearchSubmit: function() {
      this.performSearch();
    },

    onClearSearch: function() {
      this.searchQuery = '';
      this.performSearch();
    },

    onClearUserFilter: function() {
      this.selectedUser = null;
    },

    performSearch: function() {
      // Reset pagination to first page when searching
      this.searchFilters = {
        search: this.searchQuery.trim(),
        searchScope: this.searchScope
      };

      // Add date filters if they exist
      if (this.dateFrom) {
        this.searchFilters.date_from = moment(this.dateFrom).startOf('day').format('YYYY-MM-DDTHH:mm');
      }
      if (this.dateTo) {
        this.searchFilters.date_to = moment(this.dateTo).endOf('day').format('YYYY-MM-DDTHH:mm');
      }
      if (this.dateFrom || this.dateTo) {
        this.searchFilters.date_type = this.dateType;
      }

      // Add user filters if they exist
      if (this.selectedUser) {
        if (this.userFilterType === 'both') {
          this.searchFilters.created_by = this.selectedUser;
          this.searchFilters.updated_by = this.selectedUser;
        } else if (this.userFilterType === 'created_by') {
          this.searchFilters.created_by = this.selectedUser;
        } else if (this.userFilterType === 'updated_by') {
          this.searchFilters.updated_by = this.selectedUser;
        }
      }

      this.relationPagination = {
        ...this.relationPagination,
        page: 1
      };

      // Trigger the API call with search in filters parameter
      const { relationPagination, projectUid, searchFilters } = this;
      if (this.isUseOrgWithCategory) {
        this.getRelation({ 
          projectUid: projectUid, 
          pagination: relationPagination,
          filters: searchFilters
        });
      }
    },

    onDateRangeChange: function(value) {
      this.dateFrom = value[0] || null;
      this.dateTo = value[1] || null;
      this.performSearch();
    },

    formatDate: function(dateString) {
      if (!dateString) return null;
      
      try {
        const date = new Date(dateString);
        // Check if date is valid
        if (isNaN(date.getTime())) return null;
        
        const options = { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        };
        return date.toLocaleDateString('en-US', options);
      } catch (error) {
        return null;
      }
    }
  },

  components: {
    SidePanel,
    LoadContent,
    FormAddUpdateRelationship,
    FormTagAssociation,
    Datatable,
    InfoTip,
    FormTagCreate,
    Loader,
    RelationEditButton,
    RelationDeleteButton,
    InputText,
    RelationTag,
    DateRangePicker,
    InputSelect
  },

  mixins: [runShortcut('addTag', 'addRow')],

}
</script>

<style lang="scss" scoped>
  .containers-div {
    ul.nav {
      list-style-type: none;
      padding: 0;
      margin: 0;
      display: flex;
      width: 100%;
      margin-bottom: 20px;
      
      li {
        margin-right: 10px;

        a {
          &.active {
            color: $gray;
            pointer-events: none;
          }
        }
      }
    }

    .btn.add-hierarchy,
    .btn.add-association {
      display: inline-block;
      width: auto;
      margin-top: 20px;
    }
  }

  // Search controls container
  .search-controls {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 15px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 15px;
    }
  }

  // Filters row container for date and user filters
  .filters-row {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 20px;
    flex-wrap: wrap;

    @media (max-width: 1024px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 15px;
    }
  }

  // Date filter wrapper
  .date-filter-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }

    .date-type-selector {
      .date-type-dropdown {
        :deep(.multiselect) {
          min-width: 140px;
          font-size: 13px;
        }
      }
    }

    .date-range-wrapper {
      :deep(.mx-datepicker-range) {
        width: 290px;
      }
    }
  }

  // User filter wrapper
  .user-filter-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }

    .user-type-selector {
      .user-type-dropdown {
        :deep(.multiselect) {
          min-width: 140px;
          font-size: 13px;
        }
      }
    }

    .user-selector-container {
      position: relative;
      display: flex;
      align-items: center;

      .user-dropdown {
        :deep(.multiselect) {
          min-width: 200px;
          font-size: 13px;
        }
      }

      .clear-user-filter {
        position: absolute;
        right: 24px;
        top: 50%;
        transform: translateY(-50%);
        cursor: pointer;
        color: $gray-400;
        font-size: 12px;
        padding: 4px;
        z-index: 10;
        pointer-events: auto;

        &:hover {
          color: $gray-600;
        }
      }
    }
  }

  // Search bar styles - matching tags page design
  .filter-search {
    flex-grow: 1; 
    max-width: 320px;
    margin-left: 0;
    margin-bottom: 0;

    .search-wrapper {
      position: relative;
    }

    .search-input {
      width: 100%;
      padding-right: 25px;
    }

    .fa-search {
      position: absolute;
      right: 8px;
      top: 25%;
      cursor: pointer;
      color: $gray-400;

      &:hover {
        color: $gray-600;
      }
    }

    .clear-search {
      position: absolute;
      right: 32px;
      top: 25%;
      cursor: pointer;
      color: $gray-400;

      &:hover {
        color: $gray-600;
      }
    }
  }

  // Search scope selection styles
  .search-scope {
    display: flex;
    flex-direction: row;
    gap: 8px;
    min-width: 200px;

    .scope-label {
      font-size: 14px;
      font-weight: 500;
      margin-right: 10px;
    }

    .scope-options {
      display: flex;
      gap: 15px;
      flex-wrap: wrap;

      .scope-option {
        display: flex;
        align-items: center;
        gap: 6px;
        cursor: pointer;
        font-size: 14px;
        
        input[type="radio"] {
          margin: 0;
          cursor: pointer;
        }

        span {
          user-select: none;
          color: $gray-600;
        }

        &:hover span {
          color: $gray-700;
        }
      }
    }
  }

  // Associations header styles
  .associations-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }

  // Total count display styles
  .total-count-display {
    display: flex;
    justify-content: flex-end;
  }

  .relations-table {
    width: 100%;
    position: relative;

    table.table--datatable {

      tbody {
        tr {
          border-bottom: 1px solid $gray-100;
          td {
            padding: 12px 14px;
          }
          &:last-child {
            border-bottom: none;
          }
        }
      }
    }
  }

  // Table-specific styles for the relations table
  :deep(.table--datatable) {
    &.associations-table {
      thead th {
        background-color: $gray-50;
        font-weight: 600;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 12px;
        border-bottom: 2px solid $gray-200;
      }

      tbody tr.child-row {
        border-bottom: 1px solid $gray-100;

        &:hover {
          background-color: $gray-50;
        }

        // Add thicker border after last child of each parent group
        &.last-child-of-parent {
          border-bottom: 2px solid $gray-200;
        }

        td {
          vertical-align: middle;
          padding: 10px 12px;
          font-size: 13px;
          border-left: 1px solid $off-white;

          .metadata-value {
            color: $gray-600;
            font-size: 12px;
          }

          .actions {
            display: flex;
            gap: 5px;
            align-items: center;

            .edit,
            .remove {
              cursor: pointer;
            }
          }
        }
      }
    }

    tbody tr td {
      vertical-align: top;
      padding: 12px 14px;
      
      .actions {
        margin: 0;
        width: auto;
        min-width: auto;
        display: flex;
        justify-content: flex-start;
        gap: 5px;

        .edit,
        .remove {
          cursor: pointer;
          margin: 0 5px;
        }
      }
    }

    .chid-tag-with-category {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 6px;
    }

    .parent-tag-with-category {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .category-badge {
      font-size: 10px;
      font-weight: 500;
      line-height: 1;
    }
  }
</style>